import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, fromEvent, merge, timer, Subscription } from 'rxjs';
import { AuthService } from '../components/framework/services/auth.service';
import { LoggingService } from '../../framework/logging/logging.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class TokenSessionService {
  private readonly IDLE_TIMEOUT = 90 * 60 * 1000; // 90 minutes
  private readonly TOKEN_REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes before expiry
  private readonly WARNING_TIME = 2 * 60 * 1000; // 2 minutes warning

  private lastActivity = Date.now();
  private isIdle = false;
  private sessionWarningShown = false;
  private activitySubscription: Subscription = new Subscription();
  private monitoringSubscription: Subscription = new Subscription();
  private isMonitoring = false;

  // Subject for session warning
  public sessionWarning$ = new BehaviorSubject<{show: boolean, timeLeft?: number}>({show: false});

  constructor(
    private authService: AuthService,
    private loggingService: LoggingService,
    private router: Router,
    private ngZone: NgZone
  ) {
    // Start monitoring when user becomes authenticated
    this.authService.isAuthenticated$.subscribe(isAuth => {
      if (isAuth && !this.isMonitoring) {
        this.startMonitoring();
      } else if (!isAuth && this.isMonitoring) {
        this.stopMonitoring();
      }
    });
  }

  /**
   * Start monitoring user activity and token expiration
   */
  startMonitoring(): void {
    if (this.isMonitoring) return;

    // console.log('🔄 Starting token session monitoring...');
    this.isMonitoring = true;

    this.setupActivityDetection();
    this.setupTokenMonitoring();
  }

  /**
   * Stop all monitoring
   */
  stopMonitoring(): void {
    if (!this.isMonitoring) return;

    // console.log('⏹️ Stopping token session monitoring...');
    this.isMonitoring = false;

    this.activitySubscription.unsubscribe();
    this.monitoringSubscription.unsubscribe();
    this.sessionWarning$.next({show: false});
    this.sessionWarningShown = false;
    this.isIdle = false;
  }

  /**
   * Setup user activity detection
   */
  private setupActivityDetection(): void {
    // Track various user activities
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const activityStreams = events.map(event =>
      fromEvent(document, event)
    );

    this.activitySubscription = merge(...activityStreams).subscribe(() => {
      this.ngZone.run(() => {
        this.updateLastActivity();
      });
    });

    // console.log('✅ Activity detection setup complete');
  }

  /**
   * Setup token expiration monitoring
   */
  private setupTokenMonitoring(): void {
    // Check every 30 seconds
    this.monitoringSubscription = timer(0, 30000).subscribe(() => {
      this.ngZone.run(() => {
        this.checkTokenAndSession();
      });
    });

    // console.log('✅ Token monitoring setup complete');
  }

  /**
   * Update last activity timestamp
   */
  private updateLastActivity(): void {
    const wasIdle = this.isIdle;
    this.lastActivity = Date.now();
    this.isIdle = false;

    // If user was idle and became active, hide warning
    if (wasIdle && this.sessionWarningShown) {
      this.hideSessionWarning();
    }
  }

  /**
   * Check token status and user session
   */
  private async checkTokenAndSession(): Promise<void> {
    try {
      const now = Date.now();
      const timeSinceActivity = now - this.lastActivity;
      const wasIdle = this.isIdle;

      // Update idle state
      this.isIdle = timeSinceActivity > this.IDLE_TIMEOUT;

      // Log idle state changes
      // if (this.isIdle !== wasIdle) {
        // console.log(`🔄 User idle state changed: ${this.isIdle ? 'IDLE' : 'ACTIVE'}`);
        // console.log(`Time since last activity: ${Math.floor(timeSinceActivity / 1000 / 60)} minutes`);
      // }

      // Get current user and token info using the testing method
      const authState = (this.authService as any).getTestingInfo();

      if (!authState?.currentUser) {
        // console.log('❌ No user found - stopping monitoring');
        return;
      }

      const currentUser = authState.currentUser;
      const timeUntilExpiry = currentUser.timeUntilExpiry || 0;

      // console.log('🔍 Session check:', {
      //   isIdle: this.isIdle,
      //   timeSinceActivity: Math.floor(timeSinceActivity / 1000 / 60),
      //   timeUntilExpiry: Math.floor(timeUntilExpiry / 1000 / 60),
      //   tokenExpired: currentUser.expired,
      //   hasRefreshToken: currentUser.hasRefreshToken
      // });

      // Handle token expiration scenarios
      if (timeUntilExpiry <= this.TOKEN_REFRESH_BUFFER && timeUntilExpiry > 0) {
        if (this.isIdle) {
          // User is idle and token is expiring soon - show warning
          this.showExpirationWarning(timeUntilExpiry);
        } else {
          // User is active - attempt silent renewal
          // console.log('🔄 User active, attempting silent token renewal...');
          try {
            await this.authService.renewToken();
            // console.log('✅ Silent token renewal successful');
          } catch (error) {
            // console.error('❌ Silent token renewal failed:', error);
            this.showExpirationWarning(timeUntilExpiry);
          }
        }
      } else if (timeUntilExpiry <= 0 || currentUser.expired) {
        // Token has expired
        if (this.isIdle) {
          // console.log('🚪 Token expired and user idle - logging out');
          this.forceLogout('Token expired while user was idle');
        } else {
          // Try one more renewal attempt for active users
          // console.log('🔄 Token expired but user active - attempting renewal');
          try {
            await this.authService.renewToken();
          } catch (error) {
            console.error('❌ Final renewal attempt failed');
            this.forceLogout('Token expired and renewal failed');
          }
        }
      }

    } catch (error) {
      console.error('❌ Error in session monitoring:', error);
    }
  }

  /**
   * Show session expiration warning
   */
  private showExpirationWarning(timeUntilExpiry: number): void {
    if (this.sessionWarningShown) return;

    // console.log('⚠️ Showing session expiration warning');
    this.sessionWarningShown = true;

    this.sessionWarning$.next({
      show: true,
      timeLeft: timeUntilExpiry
    });

    // Auto-logout if warning is ignored
    timer(this.WARNING_TIME).subscribe(() => {
      if (this.sessionWarningShown && this.isIdle) {
        // console.log('⏰ Warning timeout - forcing logout');
        this.forceLogout('Session warning timeout');
      }
    });
  }

  /**
   * Hide session warning
   */
  private hideSessionWarning(): void {
    if (!this.sessionWarningShown) return;

    // console.log('✅ Hiding session warning - user became active');
    this.sessionWarningShown = false;
    this.sessionWarning$.next({show: false});
  }

  /**
   * Force user logout
   */
  private forceLogout(reason: string): void {
    // console.log(`🚪 Forcing logout: ${reason}`);

    this.loggingService.logTrace(`User logged out: ${reason}`);
    this.sessionWarning$.next({show: false});
    this.stopMonitoring();

    // Use your existing logout method
    this.authService.logout();
  }

  /**
   * Extend user session (called when user clicks "Stay Logged In")
   */
  extendSession(): void {
    // console.log('⏰ User requested session extension');
    this.updateLastActivity();
    this.hideSessionWarning();

    // Try to refresh the token
    this.authService.renewToken().catch(error => {
      console.error('Failed to extend session:', error);
      this.forceLogout('Session extension failed');
    });
  }

  /**
   * Get current session info for testing
   */
  getSessionInfo(): any {
    const timeSinceActivity = Date.now() - this.lastActivity;
    return {
      lastActivity: this.lastActivity,
      timeSinceActivity: timeSinceActivity,
      isIdle: this.isIdle,
      sessionWarningShown: this.sessionWarningShown,
      idleTimeoutMinutes: this.IDLE_TIMEOUT / 60000,
      timeSinceActivityMinutes: Math.floor(timeSinceActivity / 60000),
      isMonitoring: this.isMonitoring
    };
  }

  /**
   * Testing method to simulate idle state
   */
  simulateIdle(minutesAgo: number = 16): void {
    // console.log(`🧪 TESTING: Simulating ${minutesAgo} minutes of idle time`);
    this.lastActivity = Date.now() - (minutesAgo * 60 * 1000);
    this.isIdle = true;
  }

  /**
   * Testing method to configure timeouts
   */
  setTestTimeouts(idleMinutes: number = 1, warningSeconds: number = 30): void {
    // console.log(`🧪 TESTING: Setting idle timeout to ${idleMinutes} minutes, warning to ${warningSeconds} seconds`);
    (this as any).IDLE_TIMEOUT = idleMinutes * 60 * 1000;
    (this as any).WARNING_TIME = warningSeconds * 1000;
  }

  /**
   * Get monitoring status
   */
  getMonitoringStatus(): boolean {
    return this.isMonitoring;
  }

  /**
   * Destroy the service (cleanup)
   */
  destroy(): void {
    this.stopMonitoring();
  }
}